import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFirestore } from "../firebase/FirestoreContext";
import { getDocs, collection, addDoc, updateDoc, deleteDoc, Timestamp, doc, serverTimestamp } from "firebase/firestore";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// MUI components for rating and text fields
import Rating from "@mui/material/Rating";
import TextField from "@mui/material/TextField";

const InitialReviews = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const navigate = useNavigate();
  const location = useLocation();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  // States for products & selection
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // States for reviews & selected review
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);

  // New states for review form fields.
  const [rating, setRating] = useState(0);
  const [reviewTitle, setReviewTitle] = useState("");
  const [review, setReview] = useState("");
  const [userName, setUserName] = useState("");
  const [verification, setVerification] = useState("");

  // Fetch products list.
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, `${collectionPath}Products/ProductsList/DataBase`));
        const prods = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(prods);
      } catch (err) {
        console.error("Error fetching products:", err);
        toast.error("Error fetching products");
      }
    };
    fetchProducts();
  }, [db, collectionPath]);

  // When a product is selected, fetch its reviews where initial_review is true.
  useEffect(() => {
    const fetchReviews = async () => {
      if (!selectedProduct) {
        setReviews([]);
        return;
      }
      try {
        const reviewsSnapshot = await getDocs(collection(db, `${collectionPath}Products/ProductsList/DataBase/${selectedProduct.id}/Reviews`));
        const revs = reviewsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((review) => review.initial_review === true);
        setReviews(revs);
      } catch (err) {
        console.error("Error fetching reviews:", err);
        toast.error("Error fetching reviews");
      }
    };

    fetchReviews();
  }, [db, collectionPath, selectedProduct]);

  // Handle product click.
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    // Reset review form fields.
    setSelectedReview(null);
    setRating(0);
    setReviewTitle("");
    setReview("");
    setUserName("");
    setVerification("");
  };

  // Handle review click.
  const handleReviewClick = (reviewData) => {
    setSelectedReview(reviewData);
    setRating(reviewData.rating || 0);
    setReviewTitle(reviewData.title || "");
    setReview(reviewData.review || "");
    setUserName(reviewData.user_name || "");
    setVerification(reviewData.verification || "");
  };

  // Handle form submission: update if a review is selected; otherwise, add new review.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;

    try {
      const reviewsColRef = collection(db, `${collectionPath}Products/ProductsList/DataBase/${selectedProduct.id}/Reviews`);
      if (selectedReview) {
        // Update existing review.
        const reviewDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase/${selectedProduct.id}/Reviews`, selectedReview.id);
        await updateDoc(reviewDocRef, {
          rating: rating,
          title: reviewTitle,
          review: review,
          user_uid: 0,
          user_name: userName,
          verification: verification,
        });
        toast.success("Review updated successfully");
      } else {
        // Insert new review.
        await addDoc(reviewsColRef, {
          rating: rating,
          title: reviewTitle,
          review: review,
          user_uid: 0,
          user_name: userName,
          verification: verification,
          initial_review: true,
          date: serverTimestamp(),
        });
        toast.success("Review added successfully");
      }
      // Refresh reviews.
      const querySnapshot = await getDocs(reviewsColRef);
      const revs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((review) => review.initial_review === true);
      setReviews(revs);
      // Reset form fields.
      setSelectedReview(null);
      setRating(0);
      setReviewTitle("");
      setReview("");
      setUserName("");
      setVerification("");
    } catch (err) {
      console.error("Error saving review:", err);
      toast.error("Error saving review");
    }
  };

  // Handle cancel.
  const handleCancel = () => {
    setSelectedReview(null);
    setRating(0);
    setReviewTitle("");
    setReview("");
    setUserName("");
    setVerification("");
  };

  // Handle deletion of the selected review.
  const handleDelete = async () => {
    if (!selectedProduct || !selectedReview) return;
    try {
      const reviewDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase/${selectedProduct.id}/Reviews`, selectedReview.id);
      await deleteDoc(reviewDocRef);
      toast.success("Review deleted successfully");

      // Refresh reviews.
      const reviewsColRef = collection(db, `${collectionPath}Products/ProductsList/DataBase/${selectedProduct.id}/Reviews`);
      const querySnapshot = await getDocs(reviewsColRef);
      const revs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((review) => review.initial_review === true);
      setReviews(revs);
      // Reset form fields.
      setSelectedReview(null);
      setRating(0);
      setReviewTitle("");
      setReview("");
      setUserName("");
      setVerification("");
    } catch (err) {
      console.error("Error deleting review:", err);
      toast.error("Error deleting review");
    }
  };

  return (
    <div style={{ display: "flex", gap: "20px" }}>
      {/* Left side: Products list (1/3 width) */}
      <div
        style={{
          flex: "1",
          maxWidth: "33%",
          borderRight: "1px solid #ccc",
          padding: "10px",
        }}
      >
        <h3>Products</h3>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {products.map((product) => (
            <li
              key={product.id}
              onClick={() => handleProductClick(product)}
              style={{
                padding: "8px",
                cursor: "pointer",
                backgroundColor: selectedProduct && selectedProduct.id === product.id ? "#eaebec" : "transparent",
                borderBottom: "1px solid #ccc",
              }}
            >
              <div>
                <span style={{ fontWeight: "500" }}>{product.product_name}</span>, <span style={{ fontWeight: "300", opacity: 0.6 }}>{product.brand_name}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Right side: Reviews list & form (2/3 width) */}
      <div style={{ flex: "2", padding: "10px" }}>
        {selectedProduct ? (
          <>
            <h3>Initial Reviews for: {selectedProduct.product_name}</h3>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {reviews.map((reviewData) => (
                <li
                  key={reviewData.id}
                  onClick={() => handleReviewClick(reviewData)}
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    backgroundColor: selectedReview && selectedReview.id === reviewData.id ? "#eaebec" : "transparent",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  {/* Display review title and review content */}
                  <div>
                    <strong>{reviewData.title}</strong>
                  </div>
                  <div>{reviewData.review}</div>
                  <div>Rating: {reviewData.rating}</div>
                </li>
              ))}
            </ul>
            <h4>{selectedReview ? "Edit Review" : "Add New Review"}</h4>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "10px" }}>
                <span style={{ marginRight: "8px" }}>Rating:</span>
                <Rating
                  name="rating"
                  value={rating}
                  onChange={(event, newValue) => {
                    setRating(newValue);
                  }}
                />
              </div>
              <div style={{ marginBottom: "10px" }}>
                <TextField label="Title" value={reviewTitle} onChange={(e) => setReviewTitle(e.target.value)} fullWidth />
              </div>
              <div style={{ marginBottom: "10px" }}>
                <TextField label="Review" value={review} onChange={(e) => setReview(e.target.value)} fullWidth multiline minRows={3} />
              </div>
              <div style={{ marginBottom: "10px" }}>
                <TextField label="User Name" value={userName} onChange={(e) => setUserName(e.target.value)} fullWidth />
              </div>
              <div style={{ marginBottom: "10px" }}>
                <TextField label="Verification" value={verification} onChange={(e) => setVerification(e.target.value)} fullWidth />
              </div>
              <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                <button type="submit">{selectedReview ? "Update" : "Submit"}</button>
                <button type="button" onClick={handleCancel}>
                  Cancel
                </button>
                {selectedReview && (
                  <button type="button" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            </form>
          </>
        ) : (
          <div>Please select a product to view reviews.</div>
        )}
      </div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
    </div>
  );
};

export default InitialReviews;
